import { z } from 'zod';
// Paid strategy refresh proposal payload (the autopilot roadmap (CommitMind)
// A5-rotate / A6-rotate, revised).
//
// Daily scheduler emits one of these per (campaign × channel) when:
//   - EXHAUSTED: every angle in the existing strategy has been
//     drafted AND ≥1 of those deliverables is autopaused.
//   - STALE: strategy.generatedAt is >14d old.
//
// Founder reads the receipts (which angles worked, which mag
// paused + why) and decides:
//   - accept → runs the strategy proposer synchronously; new
//     angles land on the campaign's draft; the autodrafter picks
//     them up on its next tick.
//   - dismiss → state flips, no refresh fires. Cooldown still
//     applies via the audit log so the founder isn't re-asked
//     within the cooldown window.
//
// Mag-voice summary is TEMPLATED at scheduler time (no LLM cost
// for the proposal itself). The Sonnet round-trip only happens on
// accept — that's where the actual strategy proposer runs.
/** One auto-paused angle's receipts. The hook (or first headline
 *  for google) gives the founder enough context to recognize the
 *  ad; autopause reason is the same string mag stamped when she
 *  paused it. */
const failingAngleSchema = z.object({
    /** meta: deliverable.hook. google: first headline or campaign_name. */
    label: z.string().min(1).max(280),
    /** auto_pause_reason verbatim, e.g. "ctr 0.4% over 4 days at
     *  $11.20 spend — well below ~1.5% baseline. paused." */
    reason: z.string().min(1).max(280),
});
export const paidStrategyRefreshProposalPayloadSchema = z.object({
    kind: z.literal('paid_strategy_refresh_proposal'),
    /** Card lifecycle.
     *
     *  open → founder hasn't decided.
     *  accepted → founder clicked accept; strategy proposer ran;
     *    `acceptedStrategyGeneratedAt` + `costUsd` populated.
     *  dismissed → founder explicitly declined.
     *  expired → card lived past the freshness window (>30d) without
     *    action; next scheduler tick produces a fresh one.
     */
    state: z.enum(['open', 'accepted', 'dismissed', 'expired']),
    /** Which channel this refresh proposal is about. */
    channel: z.enum(['meta_paid', 'google_paid']),
    /** Detection trigger.
     *
     *  exhausted — every angle of the current strategy has been drafted
     *    and at least one is auto-paused.
     *  stale — the strategy is older than the freshness threshold.
     *  stranded — the campaign has REAL launched paid deliverables on
     *    this channel and not one of them is still running. This is the
     *    trigger that reaches a BRIEFED campaign: the two above both
     *    require draft.<channel>PaidStrategy to exist, and a campaign the
     *    founder briefed has none, so an autopause on it produced no
     *    proposal at all. Mag paused the only ad fiscalo had and the
     *    successor pipeline never saw it. */
    reason: z.enum(['exhausted', 'stale', 'stranded']),
    /** Magig campaign uuid so the renderer can deep-link. */
    campaignId: z.string().uuid(),
    /** The strategy.generatedAt being proposed for refresh.
     *  Snapshot at scheduler tick — even if the strategy mutates
     *  after, this anchors what the founder is being asked about.
     *
     *  OPTIONAL because reason='stranded' fires on campaigns that have
     *  no strategy at all; there is no generatedAt to anchor to, and
     *  inventing one would claim we refreshed something that never
     *  existed. */
    currentStrategyGeneratedAt: z.string().optional(),
    /** Mag-voice one-paragraph summary (≤500 chars). Templated, no
     *  LLM call. Names what was tested, what landed, what didn't,
     *  and what mag would do next. */
    summary: z.string().min(1).max(500),
    /** Count of drafted angles from the current strategy still
     *  running (no auto_paused_at). Surfaced as "N of M angles
     *  still working" on the card. */
    workingAngleCount: z.number().int().min(0).max(20),
    /** Up to 5 autopaused angles with their reasons. The renderer
     *  shows these inline so the founder sees mag's receipts. */
    failingAngles: z.array(failingAngleSchema).max(5),
    /** Set when state flips to accepted/dismissed. ISO 8601. */
    decidedAt: z.string().datetime().optional(),
    /** Set on accept — generatedAt of the post-refresh strategy. */
    acceptedStrategyGeneratedAt: z.string().optional(),
    /** Set on accept — Sonnet cost of the proposer call. */
    costUsd: z.number().min(0).optional(),
});
//# sourceMappingURL=paid-strategy-refresh.js.map